/**
 * Take history strip — Pro Tools take chips, color-coded by status.
 *
 * Renders previous takes (T01_v01, T02_v01, ..., T01_v02 after pickup) as a
 * horizontal scrolling strip of PT-style take chips.
 *
 * Color rules:
 *   - current/active take → green LCD-style accent border
 *   - selected best take  → yellow PT highlight fill + gold star ★
 *   - pending/uploading   → grey dashed border
 *   - regular take        → PT chrome panel
 */
import { Spinner } from '@ohear/ui'
import { useCueTakes, useMarkBestTake } from '../../lib/api/takes'
import { cx } from './cx'
import { useRecordingStore } from './state'

export interface TakeHistoryProps {
  cueId: string | null
  /** Audio playback callback — typically wired to a shared <audio> ref. */
  onPlay: (previewUrl: string) => void
}

const EMPTY_TAKES: readonly never[] = []

export function TakeHistory({ cueId, onPlay }: TakeHistoryProps) {
  const remoteTakes = useCueTakes(cueId)
  const localTakes = useRecordingStore((s) =>
    cueId ? (s.takesByCue[cueId] ?? EMPTY_TAKES) : EMPTY_TAKES,
  )
  const markBest = useMarkBestTake()

  if (!cueId) return null
  if (remoteTakes.isLoading) {
    return (
      <div className="flex items-center gap-2 text-[12px] text-pt-text-soft">
        <Spinner /> Loading take history…
      </div>
    )
  }

  const remote = remoteTakes.data?.takes ?? []
  const localOnly = localTakes.filter(
    (t) =>
      !remote.some(
        (r) =>
          (r.duration_sec ?? null) === t.durationSec && (r.take_number ?? null) === t.takeNumber,
      ),
  )

  if (remote.length === 0 && localOnly.length === 0) {
    return (
      <div className="rounded-sm border border-dashed border-pt-border-soft bg-pt-panel2 px-4 py-5 text-center pt-mono text-[10px] uppercase tracking-[0.15em] text-pt-text-soft">
        No takes yet for this cue
      </div>
    )
  }

  return (
    <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
      {remote.map((t) => {
        const isBest = t.is_best_take
        return (
          <div
            key={t.take_id}
            className={cx(
              'group relative flex shrink-0 flex-col gap-1.5 rounded-sm border px-3 py-2 text-[11px] transition-colors',
              isBest
                ? 'border-pt-text bg-pt-highlight shadow-[inset_0_1px_0_rgba(255,255,255,0.6),0_1px_2px_rgba(0,0,0,0.18)]'
                : 'border-pt-border bg-pt-panel2 shadow-[inset_0_1px_0_rgba(255,255,255,0.55),0_1px_1px_rgba(0,0,0,0.12)]',
              'min-w-[156px]',
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="pt-mono text-[11px] tabular-nums font-semibold text-pt-text">
                T{(t.take_number ?? 0).toString().padStart(2, '0')}
                <span className="text-pt-text-soft">_v</span>
                {(t.version_number ?? 1).toString().padStart(2, '0')}
              </span>
              {isBest && (
                <span
                  className="pt-mono text-[10px] font-bold text-[#b8860b]"
                  title="Marked as best take"
                >
                  ★ BEST
                </span>
              )}
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="pt-lcd pt-lcd--green text-[10px]">
                {(t.duration_sec ?? 0).toFixed(2)}s
              </span>
              {t.qc_summary && (
                <span
                  className={cx(
                    'pt-mono rounded-sm border px-1.5 py-px text-[9px] uppercase',
                    t.qc_summary === 'fail'
                      ? 'border-pt-rec bg-pt-rec text-white'
                      : t.qc_summary === 'warn'
                        ? 'border-pt-text bg-pt-warn text-pt-text'
                        : 'border-[#16a34a] bg-[#dcfce7] text-[#166534]',
                  )}
                >
                  {t.qc_summary}
                </span>
              )}
            </div>
            <div className="mt-1 flex gap-1">
              <button
                type="button"
                disabled={!t.preview_url}
                onClick={() => t.preview_url && onPlay(t.preview_url)}
                className="pt-btn flex-1 px-2 py-1 text-[10px]"
              >
                ▶ Play
              </button>
              {!t.is_best_take && (
                <button
                  type="button"
                  onClick={() => markBest.mutate({ takeId: t.take_id, cueId })}
                  disabled={markBest.isPending}
                  aria-label={`Mark take ${t.take_number ?? '?'} as best`}
                  data-testid="mark-best-button"
                  data-take-id={t.take_id}
                  className="pt-btn px-2 py-1 text-[10px]"
                >
                  ★ Mark as best
                </button>
              )}
            </div>
          </div>
        )
      })}
      {localOnly.map((t) => (
        <div
          key={t.takeId}
          className="flex shrink-0 flex-col gap-1.5 rounded-sm border border-dashed border-pt-border-soft bg-pt-panel2 px-3 py-2 text-[11px] min-w-[156px] opacity-80"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="pt-mono text-[11px] tabular-nums text-pt-text-soft">
              T{(t.takeNumber ?? 0).toString().padStart(2, '0')}
              <span className="text-pt-text-soft">_v</span>
              {(t.versionNumber ?? 1).toString().padStart(2, '0')}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="pt-lcd pt-lcd--amber text-[10px]">
              {(t.durationSec ?? 0).toFixed(2)}s
            </span>
            <span
              className={cx(
                'pt-mono rounded-sm border px-1.5 py-px text-[9px] uppercase',
                t.uploadStatus === 'failed'
                  ? 'border-pt-rec bg-pt-rec text-white'
                  : 'border-pt-border bg-pt-panel text-pt-text',
              )}
            >
              {t.uploadStatus}
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}
